import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Box } from '@mui/material'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import classNames from 'classnames/bind'

import { selectRestaurant } from '~/redux/restaurantSlice'
import { menuTemplate, menuTemplateDefault } from '~/constants/menu'
import { MenuTemplateMap } from '../templates'
import styles from '../styles/Menu.module.css'
import { isMenuTemplate } from '~/utils'
import { EXTERNAL_PARTNER } from '~/constants'
import { useTranslation } from 'react-i18next'
import { isBuffetDishType } from '~/utils/buffet'
const cx = classNames.bind(styles)

// Component hiển thị Menu bao gồm: Heading category và những món ăn thuộc category đó (Món ăn thuộc category sẽ theo multiple template)
const MenuList = (props, ref) => {
  const { name, food_items: menuItems, id, category_template } = props
  const { extraFeatures, externalPartner } = useSelector(selectRestaurant)
  const isSettingMenuTemplate = isMenuTemplate(extraFeatures) // Biến để kiểm tra có cài đặt extra feature menu template hay không
  const isCategoryVideo = category_template === menuTemplate.videoDish // trường hợp có setting video menu
  const { t } = useTranslation()

  const categoryRef = useRef()

  // public những giá trị này ra để bên ngoài có thể truy cập thông qua ref
  useImperativeHandle(ref, () => ({
    getOffsetTop: () => categoryRef?.current?.offsetTop,
    getIdCategory: () => id,
  }))

  // Hàm xử lý và lấy ra style của heading category tuỳ vào điều kiện
  const getHeadingCategoryStyles = () => {
    const baseStyles = {
      heading_category: true,
      heading_category_video: isCategoryVideo,
    }

    if (isSettingMenuTemplate) {
      baseStyles.heading_category_t1 = category_template === menuTemplate.singleDish
      baseStyles.heading_category_t2 = category_template === menuTemplate.doubleDish
    }

    return baseStyles
  }

  // Hàm xử lý lấy ra tên của category
  const getNameCategory = () => {
    if (isCategoryVideo) {
      return t('titles.popular')
    }

    return name
  }

  /**
   * Hiển thị heading category theo template
   * Trường hợp không setting hiển thị default
   */
  const renderHeadingCategory = () => {
    const classHeading = cx(getHeadingCategoryStyles())

    return (
      <Box ref={categoryRef} bgcolor="baseColor.color1">
        <Box
          component="h2"
          className={classHeading}
          sx={{
            color: 'baseColor.colorText1',
          }}>
          {getNameCategory()}
        </Box>
      </Box>
    )
  }

  /** với các nhà hàng Smaregi, loại bỏ các món ăn bufft cha (food_type = 'buffet_dish')  */
  const filterFoodList = (meus) => {
    // Trong trường hợp smaregi và món ăn là buffet type thì sẽ không hiển thị. Vì chỉ cho phép order buffet từ phía merchant
    if (externalPartner === EXTERNAL_PARTNER.Smaregi) {
      return meus?.filter((item) => !isBuffetDishType(item.food_type))
    }

    return meus
  }

  const newMenuItems = filterFoodList(menuItems)

  // Hiển thị danh sách món ăn thuộc category theo template
  const renderFoodList = () => {
    let menuTemplateKey = category_template
    if (!isSettingMenuTemplate) {
      menuTemplateKey = menuTemplateDefault // trường hợp không setting -> lấy default
      if (isCategoryVideo) menuTemplateKey = menuTemplate.videoDish
    }

    const MenuTemplate = MenuTemplateMap[menuTemplateKey]

    return <MenuTemplate data={newMenuItems} />
  }

  // không hiển thị cả category nếu không có món ăn thuộc category
  if (newMenuItems?.length <= 0) return null

  return (
    <>
      {renderHeadingCategory()}
      {renderFoodList()}
    </>
  )
}

export default forwardRef(MenuList)

MenuList.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  food_items: PropTypes.array,
  category_template: PropTypes.string,
}
